import { Box, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { HandEdit } from '@internal/backstage-plugin-sql-pipeline-common';

const useStyles = makeStyles(theme => ({
  file: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.background.default,
  },
  summary: {
    cursor: 'pointer',
    padding: theme.spacing(0.75, 1.5),
    fontFamily: 'monospace',
    fontSize: 12,
  },
  diff: {
    margin: 0,
    padding: theme.spacing(1, 0),
    fontFamily: 'monospace',
    fontSize: 12,
    overflowX: 'auto',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  line: { padding: theme.spacing(0, 1.5), whiteSpace: 'pre' },
  removed: { backgroundColor: alpha(theme.palette.error.main, 0.18) },
  added: { backgroundColor: alpha(theme.palette.success.main, 0.18) },
  gap: { padding: theme.spacing(0, 1.5), color: theme.palette.text.secondary },
}));

const CHANGE_LABEL: Record<HandEdit['change'], string> = {
  modified: '밖에서 고침',
  missing: '저장소에 없음 — 저장하면 다시 생깁니다',
};

/**
 * Backstage 밖에서 고친 파일마다 「저장하면 무엇이 덮어써지는가」 를 보여준다.
 * 파일 이름만 알려주면 그 수정이 지켜야 할 것인지 판단할 수 없었다(사용자 지적).
 * 되가져오기는 하지 않는다 — `.py`·conf 는 폼으로 되돌릴 수 없는 수정이 많다.
 */
export const HandEditsDiff = (props: { edits: HandEdit[] }) => {
  const classes = useStyles();
  if (props.edits.length === 0) return null;
  return (
    <Box mt={1}>
      <Typography variant="caption" color="textSecondary" component="div">
        <strong>-</strong> 줄은 저장하면 사라지는 밖의 수정, <strong>+</strong> 줄은 저장하면
        들어가는 Backstage 설정입니다 (폼을 고치기 전 기준).
      </Typography>
      {props.edits.map(edit => (
        <details key={edit.path} className={classes.file}>
          <summary className={classes.summary}>
            {`${edit.path} — ${CHANGE_LABEL[edit.change]}`}
          </summary>
          <pre className={classes.diff}>
            {edit.hunks.map((hunk, h) => (
              <div key={h}>
                {h > 0 && <div className={classes.gap}>⋯</div>}
                {hunk.map((line, i) => (
                  <div
                    key={i}
                    className={`${classes.line} ${
                      line.kind === '-' ? classes.removed : ''
                    } ${line.kind === '+' ? classes.added : ''}`}
                  >
                    {`${line.kind} ${line.text}`}
                  </div>
                ))}
              </div>
            ))}
          </pre>
        </details>
      ))}
    </Box>
  );
};
